/**
 * useStudioStockCatalog.ts — Carrega o acervo compartilhado de imagens do Studio
 *
 * O acervo é mantido pelo painel WL e é igual para todos os clientes. Aqui só
 * lemos os itens ativos e entregamos ao resolvedor (`setStockCatalog`), que é
 * quem transforma `acervo://<chave>` no endereço real da foto na hora de
 * desenhar a página.
 *
 * Enquanto a lista não chega, a cópia embutida no próprio resolvedor garante
 * que a prévia nunca apareça sem imagem.
 *
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, acervo compartilhado — Fase 1)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { setStockCatalog } from '@/lib/studio/core/assets/stockCatalog';
import { logger } from '@/lib/logger';

/** Item do acervo exibido nas galerias de escolha de imagem. */
export interface StudioStockAsset {
  asset_key: string;
  label: string;
  topic: string;
  ratio: string;
  image_url: string;
  thumb_url: string | null;
  credit_author: string | null;
  credit_url: string | null;
}

/** Lê o acervo ativo e alimenta o resolvedor de referências. */
export function useStudioStockCatalog() {
  return useQuery({
    queryKey: ['studio-stock-assets'],
    // O acervo muda pouco: vale manter em memória por bastante tempo.
    staleTime: 15 * 60 * 1000,
    queryFn: async (): Promise<StudioStockAsset[]> => {
      const { data, error } = await supabase
        .from('studio_stock_assets')
        .select('asset_key, label, topic, ratio, image_url, thumb_url, credit_author, credit_url')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) {
        logger.warn('Studio', 'Falha ao carregar o acervo de imagens', { erro: error.message });
        return [];
      }

      const itens = (data ?? []) as StudioStockAsset[];
      setStockCatalog(
        Object.fromEntries(itens.map((item) => [item.asset_key, item.image_url])),
      );
      return itens;
    },
  });
}
